/**
 * Shared order-document (zamówienie) `.docx` filename formula.
 *
 * Single source of truth for both `apps/api`'s `Content-Disposition` header
 * (order-document.controller.ts) and `apps/web`'s `<a download>` filename
 * (use-download-order-document.ts), so a future filename-convention change
 * can't silently diverge between the server-set header and the browser's
 * save-as name (code-review finding, offer-order-document-export).
 *
 * `campaignNumber` is stripped of `"`, `\`, and control characters
 * (including `\r`/`\n`) before interpolation. `"`/`\` could otherwise break
 * out of the quoted `Content-Disposition: attachment; filename="..."`
 * attribute value; `\r`/`\n` would make Node's http layer throw
 * `TypeError: Invalid character in header content` (campaignNumber is
 * free-form user text with no format enforced — code-review finding,
 * offer-order-document-export). Every `/` — the offer-draft-numbering
 * element separator — is replaced with `-` so a composed number survives
 * whole in the file name rather than being cut to its final path segment by
 * the OS/browser's save dialog (offer-order-document / "Separators in the
 * campaign number do not split the download file name").
 */
export function buildOrderDocumentFilename(campaignNumber: string | null, offerId: number): string {
  if (campaignNumber == null) {
    return `Zamowienie_PROJEKT_oferta-${String(offerId)}.docx`;
  }
  // eslint-disable-next-line no-control-regex -- intentionally stripping control chars, incl. \r\n, from header value
  const sanitized = campaignNumber.replace(/["\\\x00-\x1f\x7f]/g, '').replaceAll('/', '-');
  return `Zamowienie_${sanitized}.docx`;
}

/**
 * Response header carrying the count of accepted-but-unpriced lines in a
 * generated order document (bug fix, offer-order-document-export). The
 * success response body is a binary `.docx`, so there is no JSON body to
 * carry this warning — `apps/api`'s `OrderDocumentController` sets it,
 * `apps/web`'s `useDownloadOrderDocument` reads it to decide whether to show
 * a warning toast. Shared here so the header name can't silently diverge
 * between the two sides.
 */
export const ORDER_DOCUMENT_UNPRICED_COUNT_HEADER = 'X-Order-Document-Unpriced-Count';
